import React, { useState, useEffect } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";

const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

// Render a significance badge for a single variant × metric.
function SignificanceBadge({ test, metric }) {
  if (!test || test.chi2 == null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <Info className="w-3 h-3" />
        no data
      </span>
    );
  }

  const liftSign = test.lift >= 0 ? "+" : "";
  // For reply_rate, higher is better; for opt_out_rate, lower is better.
  // "Significant positive" = reply_rate up OR opt_out_rate down.
  const directionGood = metric === "reply_rate" ? test.lift > 0 : test.lift < 0;
  const color = !test.significant
    ? "bg-gray-50 text-gray-600 border-gray-200"
    : directionGood
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-red-50 text-red-700 border-red-200";
  const Icon = !test.significant ? Info : directionGood ? CheckCircle2 : XCircle;

  return (
    <div className="space-y-0.5">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}
        title={`chi²=${test.chi2}, p=${test.p_value}`}
      >
        <Icon className="w-3 h-3" />
        {test.significant ? "Significant" : "Not sig."}
        <span className="font-mono ml-0.5">
          ({liftSign}{fmtPct(test.lift)})
        </span>
      </span>
      {test.underpowered && (
        <div className="flex items-center gap-1 text-[10px] text-amber-700">
          <AlertTriangle className="w-2.5 h-2.5" />
          {test.warning || "Underpowered sample"}
        </div>
      )}
    </div>
  );
}

export default function ABTestResults() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [scripts, setScripts] = useState([]);
  const [significance, setSignificance] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/campaigns").then((res) => setCampaigns(res.data.campaigns || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCampaign) {
      setScripts([]);
      setSignificance(null);
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/campaigns/${selectedCampaign}/scripts`).catch(() => ({ data: { scripts: [] } })),
      api.get(`/campaigns/${selectedCampaign}/ab-test/significance`).catch(() => ({ data: null })),
    ])
      .then(([scriptsRes, sigRes]) => {
        setScripts(scriptsRes.data.scripts || []);
        setSignificance(sigRes.data);
      })
      .finally(() => setLoading(false));
  }, [selectedCampaign]);

  if (!user) return null;

  // Index the significance results by variant id so we can look them up quickly.
  const variantTests = {};
  if (significance?.variants) {
    for (const v of significance.variants) {
      variantTests[v.id] = v.tests;
    }
  }
  const controlId = significance?.control?.id;

  const totalSends = scripts.reduce((sum, s) => sum + (s.send_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">A/B Test Results</h1>
        <select
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          value={selectedCampaign}
          onChange={(e) => setSelectedCampaign(e.target.value)}
        >
          <option value="">Select Campaign</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {!selectedCampaign ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-400">
          Select a campaign to view A/B test results
        </div>
      ) : loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : scripts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-400">
          No script variants found for this campaign
        </div>
      ) : (
        <>
          {/* Control-missing warning */}
          {significance && !significance.has_control && significance.variants.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                No script is flagged as the control in this campaign. The earliest script is being used as
                the implicit baseline — mark one script as control to make these comparisons more meaningful.
              </span>
            </div>
          )}

          {/* Summary cards per variant */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scripts.map((script) => {
              const responseRate = script.send_count > 0
                ? ((script.reply_count || 0) / script.send_count * 100).toFixed(1)
                : "0.0";
              const optOutRate = script.send_count > 0
                ? ((script.opt_out_count || 0) / script.send_count * 100).toFixed(1)
                : "0.0";
              const sendShare = totalSends > 0
                ? ((script.send_count || 0) / totalSends * 100).toFixed(0)
                : "0";
              const isControl = script.id === controlId;
              const tests = variantTests[script.id]; // undefined for control

              return (
                <div key={script.id} className={`bg-white rounded-xl shadow-sm border p-5 ${isControl ? "border-blue-300 ring-1 ring-blue-200" : "border-gray-200"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{script.label}</h3>
                      {isControl && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Control
                        </span>
                      )}
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                      {script.ab_weight}% weight
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{script.body}</p>
                  <div className="grid grid-cols-3 gap-2 text-center mb-3">
                    <div>
                      <p className="text-lg font-bold text-gray-900">{script.send_count || 0}</p>
                      <p className="text-xs text-gray-500">Sent ({sendShare}%)</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">{responseRate}%</p>
                      <p className="text-xs text-gray-500">Response</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600">{optOutRate}%</p>
                      <p className="text-xs text-gray-500">Opt-out</p>
                    </div>
                  </div>

                  {/* Significance vs control */}
                  {tests && (
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Response vs control</span>
                        <SignificanceBadge test={tests.reply_rate} metric="reply_rate" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Opt-out vs control</span>
                        <SignificanceBadge test={tests.opt_out_rate} metric="opt_out_rate" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {significance?.alpha && (
            <p className="text-xs text-gray-400">
              Significance tested at α = {significance.alpha} (Yates-corrected chi-square, df = 1).
              "Lift" is variant rate minus control rate.
            </p>
          )}
        </>
      )}
    </div>
  );
}
